<template>
  <v-container fluid>
    <!-- Page Header -->
    <v-row class="mt-n4" align="center" justify="center">
      <v-col cols="12" md="auto">
        <div class="d-flex align-center justify-center">
          <v-avatar color="primary" size="52" class="mr-3 elevation-2">
            <v-icon color="white" size="28">receipt_long</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold grey--text text--darken-2">Órdenes de Compra</div>
            <div class="text-caption grey--text">Consulta y gestiona las órdenes de compra de la organización</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row justify="center">
      <v-col cols="12">
        <search-header-po @dataFilters="changeBusiness($event)" />
      </v-col>
    </v-row>

    <!-- KPI Cards -->
    <v-row class="mb-4" v-if="total > 0">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="rounded-xl pa-4">
          <div class="d-flex align-center">
            <v-avatar color="blue lighten-5" size="44" class="mr-3">
              <v-icon color="primary">receipt_long</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption grey--text text-uppercase font-weight-medium">Total Órdenes</div>
              <div class="text-h5 font-weight-bold primary--text">{{ total }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3" v-for="(stat, idx) in kpiByStatus" :key="idx">
        <v-card outlined class="rounded-xl pa-4">
          <div class="d-flex align-center">
            <v-avatar :color="`${stat.color} lighten-5`" size="44" class="mr-3">
              <v-icon :color="stat.color">mdi-clipboard-list-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption grey--text text-uppercase font-weight-medium">{{ stat.name }}</div>
              <div class="text-h5 font-weight-bold" :class="`${stat.color}--text`">{{ stat.count }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main table card -->
    <v-card outlined class="rounded-xl">
      <v-toolbar flat class="rounded-t-xl px-2">
        <v-chip small outlined color="grey darken-1" class="mr-3">
          <v-icon left small>mdi-format-list-bulleted</v-icon>
          {{ total }} órdenes
        </v-chip>
        <v-spacer></v-spacer>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Buscar..." outlined dense hide-details
          clearable style="max-width: 240px" class="mr-3"></v-text-field>
        <v-autocomplete dense outlined v-model="selectedStatus" @change="showStatus" return-object :items="order_status"
          item-text="name" item-value="value" label="Estatus" hide-details style="max-width: 180px"
          class="mr-3"></v-autocomplete>
        <v-menu bottom offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="success" outlined v-bind="attrs" v-on="on" class="mr-2 text-none"
              :disabled="total === 0" :loading="loadingExport">
              <v-icon left small>mdi-file-excel-outline</v-icon>
              Exportar
              <v-icon right x-small>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="download_report()">
              <v-list-item-icon><v-icon color="success">mdi-file-excel-box</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Resumido</v-list-item-title>
                <v-list-item-subtitle class="text-caption">Una fila por orden</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="download_detailed_report()">
              <v-list-item-icon><v-icon color="success">mdi-file-excel-box</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Detallado</v-list-item-title>
                <v-list-item-subtitle class="text-caption">Una fila por artículo</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-badge color="error" overlap dot :value="entity_group.length > 0">
              <v-btn icon v-bind="attrs" v-on="on" @click="dialog_settings = true">
                <v-icon>settings</v-icon>
              </v-btn>
            </v-badge>
          </template>
          <span>Filtros adicionales</span>
        </v-tooltip>
      </v-toolbar>
      <v-divider></v-divider>

      <v-data-table :headers="headers" :items="orders" :loading="loading" :server-items-length="total"
        :options.sync="options" :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
        loading-text="Cargando órdenes..." class="elevation-0">
        <template v-slot:[`item.order_id`]="{ item }">
          <span class="order-link primary--text font-weight-bold" @click="showOrder(item.order_id, false)">
            {{ filLeft(item.order_id.toString(), 12, "0") }}
          </span>
        </template>

        <template v-slot:[`item.entity_name`]="{ item }">
          <span class="order-link" @click="viewEntity(item.entity_id)">{{ item.entity_name }}</span>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip outlined small :color="time_line_orders(item.last_status_id).color">
            {{ item.last_status_name }}
          </v-chip>
        </template>

        <template v-slot:[`item.total_amount`]="{ item }">
          {{ currencyFormat(getTotal(item)) }}
          <small class="grey--text ml-1">{{ item.currency_code }}</small>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="72" color="grey lighten-1">mdi-cart-off</v-icon>
            <div class="text-h6 grey--text mt-3">Sin órdenes para mostrar</div>
            <div class="text-body-2 grey--text mt-1">Selecciona un período y unidad de negocio para consultar las
              órdenes</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Filter settings dialog -->
    <v-dialog v-model="dialog_settings" width="420">
      <v-card class="rounded-xl">
        <v-card-title class="py-4">
          <v-icon left color="primary">filter_alt</v-icon>
          Filtros adicionales
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12">
              <v-autocomplete v-model="entity_group" outlined dense multiple no-data-text="No hay órdenes" hide-details
                :items="entities" label="Filtrar por suplidor" prepend-inner-icon="mdi-domain"
                clearable></v-autocomplete>
            </v-col>
            <v-col cols="12">
              <v-autocomplete v-model="project_group" outlined dense multiple no-data-text="No hay proyectos"
                hide-details :items="projects" label="Filtrar por proyecto" prepend-inner-icon="folder_open"
                clearable></v-autocomplete>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="order_id_to_search" label="Buscar por número de orden" outlined dense type="number"
                hide-details prepend-inner-icon="mdi-pound" @keyup.enter="get_order_by_id()"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="entity_group = [], project_group = [], dialog_settings = false">Cancelar</v-btn>
          <v-btn color="primary" outlined @click="applyFilters()">Aplicar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="dialogDelete" max-width="400">
      <v-card class="rounded-xl">
        <v-card-title class="text-h6">¿Confirmar eliminación?</v-card-title>
        <v-card-text>Esta acción no se puede deshacer.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDelete">Cancelar</v-btn>
          <v-btn color="error" outlined @click="deleteItemConfirm">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New order dialog -->
    <v-dialog transition="slide-x-transition" v-model="dialog" fullscreen>
      <v-card style="background-color: #f4f5fa">
        <v-toolbar flat>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Nueva Orden de Compra</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-container>
          <v-row justify="center">
            <v-col cols="12" md="11">
              <Order v-if="dialog" :data_order="[]" @save-order="save($event)" />
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Order from "@/components/Shop/Order.vue";
import SearchHeaderPO from "@/components/SearchHeaderPO.vue";
import ExcelJS from "exceljs";
import searchHeaderUrl, { queryInt, queryList } from "@/mixins/searchHeaderUrl";

// Estado de la pantalla que se refleja en la URL (copiar/pegar o refrescar lleva al mismo lugar):
// encabezado (ver mixins/searchHeaderUrl.js) + ?status=&search=&suppliers=&projects=&order=&page=&limit=&sortBy=&sortDesc=
// Los datos vienen paginados de shopping/orders_paginated; el servidor valida el acceso a unidades/sucursales.
const DEFAULT_ITEMS_PER_PAGE = 10;
const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

export default {
  components: { Order, SearchHeaderPO },
  mixins: [searchHeaderUrl],
  data: (vm) => ({
    loading: false,
    loadingExport: false,
    search: vm.$route.query.search || "",
    searchTimeout: null,
    fetchTimeout: null,
    fetchToken: 0,
    total: 0,
    status_counts: [],
    entities: [],
    options: {
      page: queryInt(vm.$route.query.page, 1) || 1,
      itemsPerPage: ITEMS_PER_PAGE_OPTIONS.includes(queryInt(vm.$route.query.limit, null))
        ? queryInt(vm.$route.query.limit, null)
        : DEFAULT_ITEMS_PER_PAGE,
      sortBy: vm.$route.query.sortBy ? [vm.$route.query.sortBy] : [],
      sortDesc: vm.$route.query.sortBy ? [vm.$route.query.sortDesc === "true"] : [],
    },
    // Filtros del encabezado (SearchHeaderPO) de la última consulta; null hasta su primera emisión
    headerFilters: null,
    // Búsqueda por número de orden (?order=); se descarta al cambiar los filtros del encabezado
    order_id_filter: queryInt(vm.$route.query.order, null),
    dialog: false,
    dialogDelete: false,
    // Placeholder con el id de la URL hasta que cargue el catálogo de estatus
    selectedStatus: queryInt(vm.$route.query.status, 0) ? { id: queryInt(vm.$route.query.status, 0) } : "",
    order_status: [],
    entity_group: queryList(vm.$route.query.suppliers).map(Number).filter((n) => !isNaN(n)),
    project_group: queryList(vm.$route.query.projects),
    projects: [],
    dialog_settings: false,
    orders: [],
    order_id_to_search: null,
    headers: [
      { text: "Orden", align: "start", value: "order_id" },
      { text: "Fecha", value: "formatted_date" },
      { text: "Sucursal", value: "location_description_long" },
      { text: "Comprador", value: "use_fullname" },
      { text: "Suplidor", value: "entity_name" },
      { text: "Monto", value: "total_amount", align: "end" },
      { text: "Status", value: "status" },
    ],

    editedIndex: -1,
    editedItem: [],
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Nueva orden" : "Editar orden";
    },
    kpiByStatus() {
      return this.status_counts.slice(0, 3).map((row) => ({
        name: row.status_name,
        color: this.time_line_orders(row.status_id).color || "grey",
        count: row.total,
      }));
    },
  },

  watch: {
    search() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.reloadFromFirstPage(), 400);
    },
    entity_group() {
      this.reloadFromFirstPage();
    },
    project_group() {
      this.reloadFromFirstPage();
    },
    options: {
      handler() {
        this.scheduleFetch();
      },
      deep: true,
    },
  },

  created() {
    // Debe ir antes de que se cree SearchHeaderPO: el encabezado lee sus valores del store
    this.applyUrlToHeader();
    this.initialize();
  },

  beforeDestroy() {
    clearTimeout(this.searchTimeout);
    clearTimeout(this.fetchTimeout);
  },

  methods: {
    syncUrl() {
      const query = this.headerUrlQuery();

      if (this.selectedStatus && this.selectedStatus.id) query.status = String(this.selectedStatus.id);
      if (this.search) query.search = this.search;
      if (this.entity_group.length) query.suppliers = this.entity_group.join(",");
      if (this.project_group.length) query.projects = this.project_group.join(",");
      if (this.order_id_filter) query.order = String(this.order_id_filter);

      if (this.options.page > 1) query.page = String(this.options.page);
      if (this.options.itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) query.limit = String(this.options.itemsPerPage);
      if (this.options.sortBy && this.options.sortBy[0]) {
        query.sortBy = this.options.sortBy[0];
        query.sortDesc = String(!!(this.options.sortDesc && this.options.sortDesc[0]));
      }

      this.replaceUrlQuery(query);
    },
    showStatus() {
      this.order_id_filter = null;
      this.reloadFromFirstPage();
    },
    changeBusiness(data) {
      // La primera emisión del encabezado es la carga inicial: respeta ?order= y ?page= de la URL
      const firstLoad = this.headerFilters === null;
      if (!firstLoad) this.order_id_filter = null;

      // El encabezado emite en `location` la lista de sucursales cuando es "Todas";
      // aquí se manda solo el id elegido (0 = todas) y el servidor resuelve el acceso.
      const location = this.$store.state.app.filters.location;
      const locationId = location !== null && typeof location === "object" ? location.value : location;
      this.headerFilters = {
        business: data.business || 0,
        location: locationId || 0,
        dateStart: data.dateStart,
        dateEnd: data.dateEnd,
      };

      this.getSuppliers();
      if (firstLoad) this.scheduleFetch();
      else this.reloadFromFirstPage();
    },
    // Filtros que se envían al servidor (sin paginación)
    requestFilters() {
      return {
        ...this.headerFilters,
        status: (this.selectedStatus && this.selectedStatus.id) || 0,
        search: this.search || "",
        suppliers: this.entity_group,
        projects: this.project_group,
        order_id: this.order_id_filter,
      };
    },
    reloadFromFirstPage() {
      if (this.options.page !== 1) {
        // El watcher de options hace la consulta
        this.options = { ...this.options, page: 1 };
        return;
      }
      this.scheduleFetch();
    },
    // Agrupa los cambios que llegan juntos (filtro + reinicio de página) en una sola consulta
    scheduleFetch() {
      if (!this.headerFilters) return;
      clearTimeout(this.fetchTimeout);
      this.fetchTimeout = setTimeout(() => this.fetchOrders(), 0);
    },
    async fetchOrders() {
      this.syncUrl();
      const token = ++this.fetchToken;
      this.loading = true;
      try {
        const response = await this.api_Post("shopping/orders_paginated", {
          ...this.requestFilters(),
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy[0],
          sortDesc: this.options.sortDesc[0],
        });
        const result = await response.json();
        if (token !== this.fetchToken) return;

        if (response.status !== 200) {
          this.$toast.warning(result.message || "No se pudieron cargar las órdenes");
          this.orders = [];
          this.total = 0;
          this.status_counts = [];
          return;
        }
        this.orders = result.data;
        this.total = result.total;
        this.status_counts = result.status_counts;
        if (this.order_id_filter && result.total === 0) {
          this.$toast.info("Orden no encontrada!");
        }
      } catch (error) {
        if (token !== this.fetchToken) return;
        this.$toast.error(error.message);
        console.error(error.message);
      } finally {
        if (token === this.fetchToken) this.loading = false;
      }
    },
    async getSuppliers() {
      try {
        const response = await this.api_Post("shopping/orders_suppliers", {
          ...this.requestFilters(),
          order_id: null,
        });
        this.entities = response.status === 200 ? await response.json() : [];
      } catch (error) {
        console.error("Error cargando suplidores", error);
        this.entities = [];
      }
    },
    // Todas las órdenes de los filtros actuales (para exportar)
    async fetchAllOrders() {
      this.loadingExport = true;
      try {
        const response = await this.api_Post("shopping/orders_paginated", {
          ...this.requestFilters(),
          page: 1,
          limit: -1,
          sortBy: this.options.sortBy[0],
          sortDesc: this.options.sortDesc[0],
        });
        const result = await response.json();
        if (response.status !== 200) {
          this.$toast.warning(result.message || "No se pudieron cargar las órdenes");
          return null;
        }
        return result.data;
      } catch (error) {
        this.$toast.error(error.message);
        return null;
      } finally {
        this.loadingExport = false;
      }
    },
    applyFilters() {
      // Si hay numero de orden, busca por id (ademas cierra el dialogo).
      // Los filtros de suplidor y proyecto son reactivos, asi que basta con cerrar.
      if (this.order_id_to_search != null && this.order_id_to_search !== "" && !isNaN(this.order_id_to_search)) {
        this.get_order_by_id();
        return;
      }
      this.dialog_settings = false;
    },
    get_order_by_id() {
      const orderId = queryInt(this.order_id_to_search, null);
      if (!orderId) {
        return;
      }
      this.order_id_filter = orderId;
      this.order_id_to_search = null;
      this.dialog_settings = false;
      this.reloadFromFirstPage();
    },
    getTotal(items) {
      if (items.order_details == null) {
        return 0;
      }
      return items.order_details.reduce((total, item) => {
        // Calcula el subtotal (price * quantity)
        const subtotal = item.price * item.quantity;
        // Calcula el impuesto (price * quantity * tax_value)
        const tax = item.price * item.quantity * item.tax_value;
        // Suma el subtotal y el impuesto al total acumulado
        return total + subtotal + tax;
      }, 0);
    },
    async initialize() {
      let result_orders = await this.api_Get("shopping/order_status");
      this.order_status = await result_orders.json();
      this.order_status.unshift({ value: 0, name: "Todos" });

      const statusId = this.selectedStatus && this.selectedStatus.id;
      this.selectedStatus =
        (statusId && this.order_status.find((s) => s.id == statusId)) || this.order_status[0].value;

      try {
        const result_projects = await this.api_Get("project/projects");
        const projects = await result_projects.json();
        this.projects = (Array.isArray(projects) ? projects : [])
          .filter((p) => p.code != null && p.code !== "")
          .map((p) => ({
            text: `${p.name} (${p.code})`,
            value: p.code,
          }));
      } catch (error) {
        console.error("Error cargando proyectos", error);
      }
    },

    editItem(item) {
      this.editedIndex = this.entitys.indexOf(item);
      this.editedItem = Object.assign({}, item);

      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = this.entitys.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.entitys.splice(this.editedIndex, 1);
      this.closeDelete();
    },

    close() {
      this.dialog = false;

      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    async save(item) {
      try {
        const responseRequest = await this.api_Post("shopping/order_register", item);

        if (responseRequest.status == 200) {
          this.fetchOrders();
          this.$toast.success("Registrado con exito!");
          this.dialog = false;
        } else {
          const response = await responseRequest.json();
          console.error(response.message)
          this.$toast.warning(response.message);
        }
      } catch (error) {
        console.error(error.message);
        //this.$toast.warning(error.message);
      }
    },
    getOrderProjects(order) {
      // Concatena los codigos de proyecto (distintos) presentes en el detalle de la orden.
      const details = Array.isArray(order?.order_details) ? order.order_details : [];
      const codes = [
        ...new Set(
          details
            .filter((d) => d.project_code != null && d.project_code !== "")
            .map((d) => d.project_code)
        ),
      ];
      return codes.length ? codes.join(", ") : "N/A";
    },
    getOrderReference(order) {
      return order?.order_histories?.[0]?.metadata?.reference || "";
    },
    async download_report() {
      const orders = await this.fetchAllOrders();
      if (!orders) return;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Orden de Compra");
      // Definir encabezados
      worksheet.columns = [
        { header: "Orden ID", key: "order_id", width: 10 },
        { header: "Cotizacion ID", key: "quotation_id", width: 10 },
        { header: "Unidad", key: "business_unit_description_long", width: 20 },
        { header: "Planta", key: "location_description_long", width: 20 },
        { header: "Suplidor", key: "entity_name", width: 20 },
        { header: "Monto", key: "amount", width: 20 },
        { header: "Moneda", key: "currency_code", width: 20 },
        { header: "Fecha", key: "created_at", width: 20 },
        { header: "Responsable", key: "requested_by", width: 15 },
        { header: "Estatus", key: "last_status_name", width: 15 },
        { header: "Ultima iteracion por", key: "last_user", width: 15 },
        { header: "Proyecto", key: "projects", width: 20 },
        { header: "Referencia", key: "reference", width: 20 },
        { header: "Nota", key: "comments", width: 50 },
      ];

      orders.forEach((item) => {
        worksheet.addRow({
          order_id: item.order_id,
          quotation_id: item.quotation_id,
          business_unit_description_long: item.business_unit_description_long,
          location_description_long: item.location_description_long,
          entity_name: item.entity_name,
          amount: item.order_details.reduce((acc, item) => {
            const subtotal = (item.quantity * (item.price - (item.price * item.discount)));
            return acc + subtotal;
          }, 0),
          currency_code: item.currency_code,
          created_at: new Date(item.created_at).toLocaleString(), // Formatear fecha
          requested_by: item.use_fullname,
          last_status_name: item.last_status_name,
          last_user: item.order_histories[item.order_histories.length - 1].use_fullname,
          projects: this.getOrderProjects(item),
          reference: this.getOrderReference(item),
          comments: item.comments,
          accounting_amount: parseFloat(item.accounting_amount).toFixed(2),
          note: item.note,
          use_fullname: item.use_fullname,
        });
      });

      // Generar el archivo Excel
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Reporte resumido de ordenes.xlsx`;
      link.click();
    },
    async download_detailed_report() {
      const orders = await this.fetchAllOrders();
      if (!orders) return;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Detalle de Ordenes");

      // Definir encabezados
      worksheet.columns = [
        { header: "Orden ID", key: "order_id", width: 10 },
        { header: "Cotización ID", key: "quotation_id", width: 12 },
        { header: "Unidad", key: "business_unit_description_long", width: 20 },
        { header: "Planta", key: "location_description_long", width: 20 },
        { header: "Suplidor", key: "entity_name", width: 20 },
        { header: "Fecha", key: "created_at", width: 20 },
        { header: "Item ID", key: "item_id", width: 10 },
        { header: "Request ID", key: "request_id", width: 12 },
        { header: "Artículo", key: "item_name", width: 30 },
        { header: "Cantidad Solicitada", key: "quantity", width: 18 },
        { header: "Cantidad Recibida", key: "entry_quantity", width: 18 },
        { header: "Cantidad Faltante", key: "missing_quantity", width: 18 },
        { header: "Precio", key: "price", width: 10 },
        { header: "Descuento", key: "discount", width: 10 },
        { header: "Monto", key: "amount", width: 10 },
        { header: "Moneda", key: "currency_code", width: 10 },
        { header: "Responsable", key: "requested_by", width: 20 },
        { header: "Estatus", key: "last_status_name", width: 15 },
        { header: "Ultma iteracion por", key: "last_user", width: 15 },
        { header: "Almacén", key: "warehouse_name", width: 20 },
        { header: "Proyecto", key: "project_code", width: 15 },
        { header: "Referencia", key: "reference", width: 20 },
        { header: "Nota", key: "comments", width: 30 },

        // Detalles por artículo
      ];

      // Recorrer todas las órdenes y sus detalles
      orders.forEach((order) => {
        if (Array.isArray(order.order_details)) {
          order.order_details.forEach((detail) => {
            worksheet.addRow({
              order_id: order.order_id,
              quotation_id: order.quotation_id,
              business_unit_description_long:
                order.business_unit_description_long,
              location_description_long: order.location_description_long,
              entity_name: order.entity_name,
              created_at: new Date(order.created_at).toLocaleString(),
              requested_by: order.use_fullname,
              last_status_name: order.last_status_name,
              last_user: order.order_histories[order.order_histories.length - 1].use_fullname,
              warehouse_name: detail.warehouse_name,
              project_code: detail.project_code || "N/A",
              reference: this.getOrderReference(order),
              comments: order.comments,
              item_id: detail.item_id,
              request_id: detail.request_id,
              item_name: detail.item_name,
              quantity: detail.quantity,
              entry_quantity: detail.entry_quantity,
              missing_quantity: detail.quantity - detail.entry_quantity,
              price: detail.price,
              discount: detail.discount,
              amount: (detail.price - (detail.price * detail.discount)) * detail.quantity,
              currency_code: order.currency_code
            });
          });
        }
      });

      // Generar el archivo Excel
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Reporte detallado de ordenes.xlsx`;
      link.click();
    },
  },
};
</script>

<style scoped>
.order-link {
  cursor: pointer;
  color: var(--v-primary-base);
  font-weight: 500;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  transition: opacity 0.15s;
}

.order-link:hover {
  opacity: 0.65;
}
</style>
